<template>
  <el-dialog
    :title="$t('components.createManufacturingItemDialog.title')"
    :visible="visible"
    @open="onOpen"
    @closed="onClosed"
    @update:visible="setVisibility($event)"
  >
    <p v-t="'components.createManufacturingItemDialog.noOrderItemText'"></p>

    <el-divider/>

    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      @submit.native.prevent="onSubmit"
      label-width="150px"
    >
      <el-form-item
        :label="$t(getI18nPropertyKeys('manufacturingItem', 'quantity'))"
        prop="quantity"
      >
        <el-input-number v-model="form.quantity" :min="1"/>
      </el-form-item>

      <el-form-item :label="priorityLabel" prop="priority">
        <el-select v-model="form.priority">
          <el-option v-for="priority in priorities"
            :key="priority"
            :label="translateValue('manufacturingItem', 'priority', priority)"
            :value="priority"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="productNameLabel" prop="productNameId">
        <el-select
          v-model="form.productNameId"
          filterable
          placeholder=""
        >
          <el-option v-for="productName in productNames"
            :key="productName.id"
            :label="translateValue('orderItem', 'productName', productName.name, {
              ignoreMissingTranslation: true,
            })"
            :value="productName.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="productCategoryLabel" prop="productCategoryId">
        <el-select
          v-model="form.productCategoryId"
          filterable
          placeholder=""
        >
          <el-option v-for="productCategory in productCategories"
            :key="productCategory.id"
            :label="translateValue('orderItem', 'productCategory', productCategory.name)"
            :value="productCategory.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="manufacturingDefectLabel" prop="manufacturingDefect">
        <el-checkbox v-model="form.manufacturingDefect"/>
      </el-form-item>

      <el-form-item :label="complaintTypeLabel" prop="complaintTypeId">
        <el-select
          v-model="form.complaintTypeId"
          filterable
          placeholder=""
          clearable
        >
          <el-option v-for="complaintType in complaintTypes"
            :key="complaintType.id"
            :label="translateValue('manufacturingItem', 'complaintType', complaintType.name)"
            :value="complaintType.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="materialLabel" prop="materialId">
        <el-select
          v-model="form.materialId"
          filterable
          placeholder=""
          clearable
        >
          <el-option v-for="material in materials"
            :key="material.id"
            :label="translateValue('orderItem', 'material', material.name)"
            :value="material.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="lamellaTypeLabel" prop="lamellaTypeId">
        <el-select
          v-model="form.lamellaTypeId"
          filterable
          placeholder=""
          clearable
        >
          <el-option v-for="lamellaType in lamellaTypes"
            :key="lamellaType.id"
            :label="translateValue('orderItem', 'lamellaType', lamellaType.name)"
            :value="lamellaType.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="plateThicknessLabel" prop="plateThickness">
        <el-input-number v-model="form.plateThickness" clearable/>
      </el-form-item>

      <el-form-item :label="legProfileLabel" prop="legProfile">
        <el-input v-model="form.legProfile" clearable/>
      </el-form-item>

      <el-form-item :label="manufacturerLabel" prop="manufacturerId">
        <el-select
          v-model="form.manufacturerId"
          filterable
          placeholder=""
        >
          <el-option v-for="manufacturer in manufacturers"
            :key="manufacturer.id"
            :label="manufacturer.name"
            :value="manufacturer.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="deliverToLabel" prop="deliverToId">
        <el-select
          v-model="form.deliverToId"
          filterable
          placeholder=""
          clearable
        >
          <el-option v-for="deliverTo in deliverTos"
            :key="deliverTo.id"
            :label="translateValue('orderItem', 'deliverTo', deliverTo.name)"
            :value="deliverTo.id"
          />
        </el-select>
      </el-form-item>

      <el-divider/>

      <el-form-item class="d-flex justify-content-end mb-0">
        <el-button @click="onCancel" v-t="'common.cancel'"/>
        <el-button type="primary" native-type="submit" v-t="'common.submit'"/>
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script>
import { omit, sortBy } from 'lodash';
import { mapState } from 'vuex';
import { getStatusByName } from '@/store';
import { priorities, Priority } from '@/models/priority';
import { buildColumns } from '@/lib/data-grid-columns';
import {
  getI18nPropertyKeys,
  translateProperty,
  translateValue,
} from '@/lib/util';

export default {
  name: 'CreateManufacturingItemDialog',
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        complaintTypeId: null,
        deliverToId: undefined,
        lamellaTypeId: undefined,
        legProfile: '',
        manufacturerId: undefined,
        manufacturingDefect: false,
        materialId: undefined,
        quantity: 1,
        plateThickness: undefined,
        productCategoryId: undefined,
        productNameId: undefined,
        priority: Priority.NORMAL,
      },
      rules: {
        manufacturerId: {
          required: true,
          type: 'number',
          fullField: this.$t('entities.manufacturer.name'),
        },
        productCategoryId: { required: true },
        productNameId: { required: true },
      },
      manufacturers: [],
      priorities,
    };
  },
  computed: {
    ...mapState(['setElements']),
    complaintTypeLabel() {
      return translateProperty('manufacturingItem', 'complaintTypeId');
    },
    deliverToLabel() {
      return translateProperty('orderItem', 'deliverToId');
    },
    lamellaTypeLabel() {
      return translateProperty('orderItem', 'lamellaTypeId');
    },
    legProfileLabel() {
      return translateProperty('orderItem', 'legProfile');
    },
    manufacturerLabel() {
      return this.$t('entities.manufacturer.name');
    },
    manufacturingDefectLabel() {
      return translateProperty('manufacturingItem', 'manufacturingDefect');
    },
    materialLabel() {
      return translateProperty('orderItem', 'materialId');
    },
    plateThicknessLabel() {
      return translateProperty('orderItem', 'plateThickness');
    },
    priorityLabel() {
      return translateProperty('manufacturingItem', 'priority');
    },
    productCategoryLabel() {
      return translateProperty('orderItem', 'productCategoryId');
    },
    productNameLabel() {
      return translateProperty('orderItem', 'productNameId');
    },
    complaintTypes() {
      return this.getSetElementsFor('manufacturingItem', 'complaintType');
    },
    deliverTos() {
      return this.getSetElementsFor('orderItem', 'deliverTo');
    },
    lamellaTypes() {
      return this.getSetElementsFor('orderItem', 'lamellaType');
    },
    materials() {
      return this.getSetElementsFor('orderItem', 'material');
    },
    productCategories() {
      return this.getSetElementsFor('orderItem', 'productCategory');
    },
    productNames() {
      return this.getSetElementsFor('orderItem', 'productName', { ignoreMissingTranslation: true });
    },
  },
  methods: {
    buildColumns,
    getI18nPropertyKeys,
    translateValue,
    setVisibility(value) {
      this.$emit('update:visible', value);
    },
    reset() {
      this.$refs.form.resetFields();
    },
    close() {
      this.setVisibility(false);
    },
    async loadManufacturers() {
      const { data } = await this.$http.get('/manufacturers', {
        params: { filter: { order: ['name'] } },
      });

      this.manufacturers = data;
    },
    getSetElementsFor(entity, property, options = {}) {
      const set = `entities.${entity}.values.${property}`;
      const filtered = this.setElements.filter(setElement => setElement.set === set);
      const comparator = setElement => translateValue(entity, property, setElement.name, options);

      return sortBy(filtered, comparator);
    },
    async createManufacturingItem() {
      const now = new Date();

      await this.$http.post('/manufacturing-items', {
        ...omit(this.form, ['legProfile']),
        legProfile: this.form.legProfile || null,
        orderedAt: now,
        statusId: getStatusByName(this.$store.state, 'initial').id,
      });

      this.$emit('created:manufacturing-item');
    },
    async onOpen() {
      await this.loadManufacturers();
    },
    onClosed() {
      this.reset();
    },
    onCancel() {
      this.close();
    },
    onSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          await this.createManufacturingItem();
          this.close();
        } else {
          this.$message({
            message: this.$t('common.form.resolveErrorsBeforeSubmit'),
            type: 'warning',
          });
        }
      });
    },
  },
};
</script>

<style scoped>

</style>
